import { useEffect, useMemo, useState } from 'react'
import { naira, lagosToday, nightsBetween, addDays, cyclesFor, friendlyStayError } from '../lib/format'
import {
  createStay, findOrCreateGuest, loadBranchStaySettings, loadFreeRooms, loadPriorBalance, searchSimilarGuests, loadUpcomingReservations,
} from '../lib/data'
import { useToast } from '../components/Toast'
import Notice from '../components/Notice'
import { tracked } from '../lib/loadfail'

const RATE_FIELDS = {
  standard: 'rate_standard', alternate: 'rate_alternate',
  short: 'rate_short', special: 'rate_special',
}

// Branch labels already end in the word "Rate" at Nnewi — "Season Rate",
// "Off-Season Rate". The screen used to append its own, so the summary
// read "Season Rate rate, ₦20,000 × 2 nights" and the warning read "not
// the room's usual season rate rate". The label is the name of the rate;
// this just stops a second "rate" being stuck on the end of one.
const rateName = (label = '') => String(label).replace(/\s*rate\s*$/i, '').trim() || label

export default function CheckIn({ boot, onDone }) {
  const { staff } = boot
  const toast = useToast()
  const [rooms, setRooms] = useState(null)
  const [allowedCycles, setAllowedCycles] = useState(null)
  const [rateLabels, setRateLabels] = useState({ standard: 'Standard', alternate: 'Discounted', short: 'Short-time' })
  const [busy, setBusy] = useState(false)

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  // Set when the person picks a suggestion from the name-matching
  // dropdown below — that tap IS the confirmation of who this is, more
  // reliable than any single field, since phone is often withheld and
  // names collide. Cleared whenever they go back to typing, since
  // editing the name or phone after confirming means they are not sure
  // it was a match after all.
  const [confirmedGuestId, setConfirmedGuestId] = useState(null)
  // Checked before a room is even chosen, so front desk sees an old
  // balance while there is still time to settle it or decide to carry
  // it forward, not after the new stay is already open.
  const [priorBalance, setPriorBalance] = useState(0)
  // Who the OLD stay was billed to, if staff typed one — so "owes 30,000"
  // is never read as the occupant's own debt when it was someone else's.
  const [priorBilledTo, setPriorBilledTo] = useState(null)
  useEffect(() => {
    if (confirmedGuestId) return   // already known — see the dropdown handler
    const digits = phone.replace(/\D/g, '')
    if (digits.length < 7) { setPriorBalance(0); setPriorBilledTo(null); return }
    const t = setTimeout(async () => {
      // Read-only lookup — never creates a guest, unlike
      // findOrCreateGuest, which this box must not trigger on every
      // keystroke for someone who may never actually check in.
      try {
        const r = await loadPriorBalance(staff.branch_id, name, phone)
        setPriorBalance(r.amount); setPriorBilledTo(r.billedTo)
      } catch { setPriorBalance(0); setPriorBilledTo(null) }
    }, 400)
    return () => clearTimeout(t)
  }, [phone, name, staff.branch_id, confirmedGuestId])
  const [roomId, setRoomId] = useState('')
  const [rateType, setRateType] = useState('standard')
  const [rateOverride, setRateOverride] = useState('')
  const [checkIn, setCheckIn] = useState(lagosToday())
  const [scheduledOut, setScheduledOut] = useState(addDays(lagosToday(), 1))
  const [cycle, setCycle] = useState('one_off')
  const [reserve, setReserve] = useState(false)
  const [billTo, setBillTo] = useState('')
  const [billToGuestId, setBillToGuestId] = useState(null)
  const [billToSuggestions, setBillToSuggestions] = useState([])
  const [billToOpen, setBillToOpen] = useState(false)
  const [similarGuests, setSimilarGuests] = useState([])
  const [suggestionsOpen, setSuggestionsOpen] = useState(false)

  // Debounced — a similar-name search on every keystroke would be
  // wasteful and flickery. 400ms is enough to wait for a pause in
  // typing without feeling laggy.
  useEffect(() => {
    if (!suggestionsOpen) return
    const t = setTimeout(() => {
      searchSimilarGuests(staff.branch_id, name).then(setSimilarGuests)
    }, 400)
    return () => clearTimeout(t)
  }, [name, staff.branch_id, suggestionsOpen])

  useEffect(() => {
    if (!billToOpen) return
    const t = setTimeout(() => {
      searchSimilarGuests(staff.branch_id, billTo).then(setBillToSuggestions)
    }, 400)
    return () => clearTimeout(t)
  }, [billTo, staff.branch_id, billToOpen])

  useEffect(() => {
    tracked('Rooms free for those dates', loadFreeRooms(staff.branch_id, checkIn, scheduledOut)).then(list => {
      setRooms(list)
      // The previously-picked room may no longer be free for the
      // newly-selected dates — don't let a stale selection through.
      setRoomId(cur => list.some(r => r.id === cur) ? cur : null)
    }).catch(() => setRooms([]))
  }, [staff.branch_id, checkIn, scheduledOut])

  // Rooms left out of the list above because someone has reserved them
  // for dates that overlap this booking — said out loud, so a missing
  // room is explained rather than a mystery.
  const [reservations, setReservations] = useState([])
  useEffect(() => {
    tracked('Bookings not yet arrived', loadUpcomingReservations(staff.branch_id)).then(setReservations).catch(() => setReservations([]))
  }, [staff.branch_id])
  const heldBack = (checkIn && scheduledOut)
    ? reservations.filter(r => r.checkIn < scheduledOut && checkIn < r.scheduledOut) : []

  useEffect(() => {
    tracked("The branch's stay settings", loadBranchStaySettings(staff.branch_id)).then(s => {
      setAllowedCycles(s.allowedCycles)
      setRateLabels(s.rateLabels)
    }).catch(() => setAllowedCycles(null))
  }, [staff.branch_id])

  const cycles = useMemo(() => cyclesFor(allowedCycles), [allowedCycles])
  useEffect(() => {
    if (cycles.length && !cycles.some(c => c.value === cycle)) setCycle(cycles[0].value)
  }, [cycles])

  const room = (rooms || []).find(r => r.id === roomId) || null
  const listRate = room ? Number(room[RATE_FIELDS[rateType]] ?? 0) : 0
  // A branch that has not named its fourth rate does not have the key at
  // all (loadBranchStaySettings), so if someone is left on a rate their
  // branch does not offer — an old session, a branch switch — put them
  // back on the first one rather than checking in on a rate that is not
  // there.
  const rateMissing = !rateLabels[rateType]
  useEffect(() => {
    if (rateMissing) { setRateType(Object.keys(rateLabels)[0] || 'standard'); setRateOverride('') }
  }, [rateMissing])
  const rate = rateOverride === '' ? listRate : Number(rateOverride)
  const nights = checkIn && scheduledOut ? nightsBetween(checkIn, scheduledOut) : 0
  const valid = name.trim() && roomId && checkIn && scheduledOut && rate >= 0

  async function submit() {
    setBusy(true)
    try {
      const guestId = await findOrCreateGuest(staff.branch_id, name, phone)
      await createStay({
        staff, guestId, roomId, rateType, dailyRate: rate, reserve,
        billingCycle: cycle, checkIn, scheduledOut, billTo: billTo.trim(), billToGuestId,
      })
      toast(reserve ? 'Reservation saved' : `Checked in — Room ${room?.room_number}`, 'success')
      onDone?.()
    } catch (e) { toast(friendlyStayError(e), 'error') }
    setBusy(false)
  }

  if (rooms === null) return <p className="px-5 text-dim">Loading…</p>

  return (
    <div className="px-5">
      <h2 className="text-2xl font-bold mt-2">New booking</h2>

      <div className="mt-5">
        <div className="text-dim mb-1">Guest name</div>
        <input value={name} onChange={e => {
            setName(e.target.value); setSuggestionsOpen(true); setConfirmedGuestId(null)
          }}
          onFocus={() => setSuggestionsOpen(true)} autoFocus
          className="h-14 w-full px-4 rounded-xl bg-surface border border-line" />
        {suggestionsOpen && !!similarGuests.length && (
          <div className="mt-2 rounded-xl border border-amber bg-surface divide-y divide-line overflow-hidden">
            <div className="px-4 py-2 text-dim text-sm">Similar guest already exists — same person?</div>
            {similarGuests.map(g => (
              <button key={g.id} type="button"
                onClick={() => {
                  setName(g.full_name); setPhone(g.phone || '')
                  // Picking this IS confirming identity — no waiting on
                  // the phone-typing effect, and the balance is shown
                  // directly from what this search already fetched.
                  setConfirmedGuestId(g.id); setPriorBalance(g.owing || 0); setPriorBilledTo(g.billedTo || null)
                  setSuggestionsOpen(false); setSimilarGuests([])
                }}
                className="block w-full text-left px-4 py-3 hover:bg-raise">
                <div className="font-semibold">{g.full_name}</div>
                {g.phone && <div className="text-dim text-sm">{g.phone}</div>}
                {/* Shown on the row itself, BEFORE anyone is picked — the
                    earlier version only revealed a balance after the tap,
                    and this line was never actually added to the list. */}
                {g.owing > 0.009 && (
                  <div className="text-clay text-sm font-semibold">
                    Owes {naira(g.owing)} from a previous stay
                    {g.billedTo ? ` · ${g.billedTo}` : ''}
                  </div>
                )}
              </button>
            ))}
            <button type="button" onClick={() => { setSuggestionsOpen(false); setSimilarGuests([]) }}
              className="block w-full text-center px-4 py-2 text-dim text-sm">
              No, this is a different person
            </button>
          </div>
        )}
      </div>
      <div className="mt-4">
        <div className="text-dim mb-1">Phone number</div>
        <input value={phone}
          onChange={e => { setPhone(e.target.value); setConfirmedGuestId(null) }} inputMode="tel"
          placeholder="Matches a returning guest to their history"
          className="h-14 w-full px-4 rounded-xl bg-surface border border-line placeholder:text-dim" />
      </div>

      {/* Surfaced BEFORE a room is chosen, not after the new stay is
          open — the moment there is still time to settle it, or decide
          to carry it forward deliberately rather than discover it later
          on an unrelated screen. */}
      {priorBalance > 0.009 && (
        <Notice tone="problem" className="mt-3"
          title={`Still owes ${naira(priorBalance)} from a previous stay`}>
          {priorBilledTo && (
            <p className="text-clay text-sm mt-1">
              ({priorBilledTo})
            </p>
          )}
          <p className="text-dim text-sm mt-1">
            This is a new booking with its own bill. The old balance does not
            carry over automatically — settle it now, or make a note to collect
            it separately.
          </p>
        </Notice>
      )}

      <div className="mt-4">
        <div className="text-dim mb-1">
          Room {rooms.length ? `(${rooms.length} free)` : '(none free)'}
        </div>
        <select value={roomId} onChange={e => { setRoomId(e.target.value); setRateOverride('') }}
          className="h-14 w-full px-4 rounded-xl bg-surface border border-line">
          <option value="">Select a room</option>
          {rooms.map(r => (
            <option key={r.id} value={r.id}>
              {r.room_number}{r.room_categories?.name ? ` — ${r.room_categories.name}` : ''}
            </option>
          ))}
        </select>
        {!!heldBack.length && (
          <div className="mt-2 rounded-xl border border-amber bg-amber/10 px-4 py-3 text-sm">
            <p className="text-amber font-semibold">Not in the list — already reserved for these dates:</p>
            {heldBack.map(r => (
              <p key={r.stayId} className="text-dim">
                Room {r.roomNumber} — {r.guest}, {r.checkIn} to {r.scheduledOut}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 flex gap-3">
        <div className="flex-1">
          <div className="text-dim mb-1">Rate type</div>
          <select value={rateType} onChange={e => { setRateType(e.target.value); setRateOverride('') }}
            className="h-14 w-full px-3 rounded-xl bg-surface border border-line">
            {Object.entries(rateLabels).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
        <div className="flex-1">
          <div className="text-dim mb-1">
            Daily rate {room && rateOverride !== '' && rate !== listRate ? `(list: ${naira(listRate)})` : ''}
          </div>
          <input type="number" inputMode="decimal"
            value={rateOverride === '' ? (listRate || '') : rateOverride}
            onChange={e => setRateOverride(e.target.value)}
            className="h-14 w-full px-3 rounded-xl bg-surface border border-line tnum" />
        </div>
      </div>

      <div className="mt-4 flex gap-3">
        <div className="flex-1">
          <div className="text-dim mb-1">Check-in date</div>
          <input type="date" value={checkIn}
            onChange={e => setCheckIn(e.target.value)}
            className="h-14 px-3 rounded-xl bg-surface border border-line tnum" />
        </div>
        <div className="flex-1">
          <div className="text-dim mb-1">Scheduled check-out</div>
          <input type="date" value={scheduledOut} min={checkIn}
            onChange={e => setScheduledOut(e.target.value)}
            className="h-14 px-3 rounded-xl bg-surface border border-line tnum" />
        </div>
      </div>

      <div className="mt-4">
        <div className="text-dim mb-1">Billing cycle</div>
        <div className="flex gap-2">
          {cycles.map(c => (
            <button key={c.value} onClick={() => setCycle(c.value)}
              className={`flex-1 h-12 rounded-xl border font-semibold ${cycle === c.value
                ? 'bg-amber text-bg border-amber' : 'border-line text-dim'}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <label className="mt-4 flex items-center gap-2 text-dim">
        <input type="checkbox" checked={reserve} onChange={e => setReserve(e.target.checked)} />
        Reservation only — guest has not arrived yet
      </label>

      <div className="mt-4">
        <div className="text-dim mb-1">Billed to (optional)</div>
        <input value={billTo}
          onChange={e => { setBillTo(e.target.value); setBillToGuestId(null); setBillToOpen(true) }}
          onFocus={() => setBillToOpen(true)}
          placeholder="Leave blank if the guest pays their own bill"
          className="h-14 w-full px-4 rounded-xl bg-surface border border-line placeholder:text-dim" />
        {billToOpen && !!billToSuggestions.length && (
          <div className="mt-2 rounded-xl border border-amber bg-surface divide-y divide-line overflow-hidden">
            <div className="px-4 py-2 text-dim text-sm">Is this an existing guest?</div>
            {billToSuggestions.map(g => (
              <button key={g.id} type="button"
                onClick={() => {
                  setBillTo(g.full_name); setBillToGuestId(g.id)
                  setBillToOpen(false); setBillToSuggestions([])
                }}
                className="block w-full text-left px-4 py-3 hover:bg-raise">
                <div className="font-semibold">{g.full_name}</div>
                {g.phone && <div className="text-dim text-sm">{g.phone}</div>}
              </button>
            ))}
            <button type="button" onClick={() => { setBillToOpen(false); setBillToSuggestions([]) }}
              className="block w-full text-center px-4 py-2 text-dim text-sm">
              No — not a guest (company, relative, etc.)
            </button>
          </div>
        )}
        <p className="text-dim text-sm mt-1">
          If someone else is responsible for this bill — a long-term guest vouching
          for a visitor, a company, a relative — note it here so it's visible from
          check-in, not just discoverable later in a payment note.
          {billToGuestId && ' Linked — this amount will also show on their own folio.'}
        </p>
      </div>

      {room && nights > 0 && (
        <div className="mt-4 rounded-xl bg-surface border border-line px-4 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-dim tnum">
              {rateName(rateLabels[rateType])} rate, {naira(rate)} × {nights} night{nights > 1 ? 's' : ''}
            </span>
            <span className="text-xl font-bold tnum">{naira(nights * rate)}</span>
          </div>
          {/* A room with no figure for this rate yet is not the same as a
              guest being given an unusual price, and saying "not the usual
              rate (₦0)" for it sends the front desk looking for a mistake
              that is not there (574). */}
          {rate !== listRate && listRate === 0 && (
            <p className="text-dim text-sm mt-2">
              No {rateName(rateLabels[rateType]).toLowerCase()} rate is set for this room yet.
              A GM can set it under Stay settings; what you type here is what
              this guest pays.
            </p>
          )}
          {rate !== listRate && listRate > 0 && (
            <p className="text-clay text-sm mt-2">
              This is not the room's usual {rateName(rateLabels[rateType]).toLowerCase()} rate ({naira(listRate)}).
              Is that what was agreed?
            </p>
          )}
          {rate === 0 && (
            <p className="text-clay text-sm mt-2">The rate is ₦0 — the guest would pay nothing.</p>
          )}
          {nights > 14 && (
            <p className="text-clay text-sm mt-2">
              That is {nights} nights. Check the check-out date is right.
            </p>
          )}
        </div>
      )}

      <button onClick={submit} disabled={busy || !valid}
        className="mt-6 w-full h-16 rounded-2xl bg-amber text-bg text-xl font-bold disabled:opacity-40">
        {busy ? 'Saving…' : reserve ? 'Save reservation' : 'Check in'}
      </button>
    </div>
  )
}
